import React, { useMemo, useState } from 'react';
import { formatPct } from './timeframes';
import { formatPrice, formatQuantity, heldFor, shortAddress } from './cryptoFormat';
import { groupByAsset } from './cryptoGroups';
import { useT } from '../../i18n';

// On-chain holdings, one row per asset, opening onto one row per token per
// wallet: SOL across five wallets, or ETH beside WETH and wstETH, is one line
// with its breakdown underneath (see `cryptoGroups`).
//
// Assets worth under $100 in total sit behind their own filter, which carries
// their combined value so that leaving them out of view never hides how much
// they add up to. The page's headline still counts them.
//
// Quantity, price and value are live. The average entry, P/L and how long it
// has been held come from the indexed history, and are dashes rather than guesses
// while it is still being indexed or where part of the cost is unknown.
//
// The ticker carries the token's logo and links out — to CoinGecko, or to
// DexScreener for a token CoinGecko does not list. Beside it, what the row IS
// when it is not simply a token held: an Aave supply or borrow, a liquid-
// staking token, or a Hyperliquid account's cash. A Hyperliquid perp carries
// no label: in spot terms it is simply the asset held.

const COLUMNS = [
    { key: 'symbol', labelKey: 'crypto.col.token', align: 'left' },
    { key: 'chain', labelKey: 'crypto.col.chain', align: 'left' },
    { key: 'wallet', labelKey: 'crypto.col.wallet', align: 'left', walletOnly: true },
    { key: 'quantity', labelKey: 'crypto.col.qty', align: 'right', numeric: true },
    { key: 'avgEntryPrice', labelKey: 'crypto.col.avgEntry', align: 'right', numeric: true },
    { key: 'currentPrice', labelKey: 'crypto.col.price', align: 'right', numeric: true },
    { key: 'value', labelKey: 'crypto.col.value', align: 'right', numeric: true },
    { key: 'pnl', labelKey: 'crypto.col.pnl', align: 'right', numeric: true, signed: true },
    { key: 'pnlPct', labelKey: 'crypto.col.pnlPct', align: 'right', numeric: true, signed: true },
    // Sorts on how long, so "longest held" is the descending end like every
    // other numeric column.
    { key: 'heldMs', labelKey: 'crypto.col.held', align: 'right', numeric: true },
];

/** Below this, an asset is listed under the small-balances filter instead. */
export const SMALL_BALANCE = 100;

/** By SIZE, not sign: a $50k borrow, or a levered Hyperliquid account's
 *  negative cash, is not a small balance. */
const isSmall = (g) => Math.abs(g.value) < SMALL_BALANCE;

const MAIN = 'main';
const SMALL = 'small';

/** The logo, or the ticker's first letter in a disc when there is none or it
 *  fails to load — so every row's ticker lines up. */
function Logo({ src, symbol }) {
    const [broken, setBroken] = useState(false);
    if (!src || broken) {
        return <span className="crypto__logo is-blank" aria-hidden="true">{symbol.slice(0, 1)}</span>;
    }
    return (
        <img
            className="crypto__logo"
            src={src}
            alt=""
            width="18"
            height="18"
            decoding="async"
            // Third-party image hosts: no need to tell them which page asked.
            referrerPolicy="no-referrer"
            onError={() => setBroken(true)}
        />
    );
}

function Ticker({ row }) {
    const { t } = useT();
    const href = row.coingeckoUrl || row.dexscreenerUrl;
    const body = (
        <>
            <Logo src={row.logoUrl} symbol={row.symbol} />
            <span className="crypto__symbol">{row.symbol}</span>
        </>
    );
    if (!href) return <span className="crypto__ticker">{body}</span>;
    const label = t('crypto.openOn', {
        symbol: row.symbol,
        site: row.coingeckoUrl ? 'CoinGecko' : 'DexScreener',
    });
    return (
        <a className="crypto__ticker is-link" href={href} target="_blank" rel="noopener noreferrer" title={label} aria-label={label}>
            {body}
        </a>
    );
}

/** "Aave · supplied", "Lido · staked" — nothing for a plain token. Nor for a
 *  Hyperliquid perp: shown in spot terms, it is meant to read as the asset
 *  held, and the chain column already says where. */
const UNLABELLED = new Set(['spot', 'perp']);

function KindBadge({ row }) {
    const { t } = useT();
    if (!row.kind || UNLABELLED.has(row.kind)) return null;
    return (
        <span className={`crypto__kind is-${row.kind}`}>
            {t(`crypto.kind.${row.kind}`, { protocol: row.protocol ?? '' })}
        </span>
    );
}

export function heldLabel(t, sinceIso, now) {
    const held = heldFor(sinceIso, now);
    if (!held) return null;
    if ('days' in held) return t('crypto.held.days', { n: held.days });
    if (held.years === 0) return t('crypto.held.months', { m: held.months });
    return t('crypto.held.yearsMonths', { y: held.years, m: held.months });
}

export default function CryptoTable({ rows, walletNames, showWallet }) {
    const { t, tn, fmt, locale } = useT();
    const [sort, setSort] = useState({ key: 'value', direction: 'desc' });
    const [view, setView] = useState(MAIN);
    const [open, setOpen] = useState(() => new Set());
    const columns = showWallet ? COLUMNS : COLUMNS.filter((c) => !c.walletOnly);

    const groups = useMemo(() => {
        const now = Date.now();
        const enrich = (r) => ({
            ...r,
            wallet: r.walletId ? walletNames.get(r.walletId) ?? r.walletId : null,
            heldMs: r.firstPurchaseAt ? now - new Date(r.firstPurchaseAt).getTime() : null,
        });
        return groupByAsset(rows).map((g) => ({
            ...enrich(g),
            members: g.members.map(enrich).sort((a, b) => b.value - a.value),
        }));
    }, [rows, walletNames]);

    const small = groups.filter(isSmall);
    const smallTotal = small.reduce((total, g) => total + g.value, 0);
    const showing = view === SMALL && small.length > 0 ? SMALL : MAIN;

    const visible = useMemo(() => {
        const column = COLUMNS.find((c) => c.key === sort.key);
        return groups
            .filter((g) => (showing === SMALL) === isSmall(g))
            .sort((a, b) => {
                const av = a[sort.key];
                const bv = b[sort.key];
                // Missing values sink whichever way the sort runs.
                if (av === null || av === undefined || av === '') return 1;
                if (bv === null || bv === undefined || bv === '') return -1;
                const cmp = column?.numeric ? av - bv : String(av).localeCompare(String(bv));
                return sort.direction === 'asc' ? cmp : -cmp;
            });
    }, [groups, showing, sort]);

    const toggleOpen = (asset) =>
        setOpen((current) => {
            const next = new Set(current);
            if (next.has(asset)) next.delete(asset);
            else next.add(asset);
            return next;
        });

    const toggleSort = (key) =>
        setSort((current) =>
            current.key === key
                ? { key, direction: current.direction === 'asc' ? 'desc' : 'asc' }
                : { key, direction: COLUMNS.find((c) => c.key === key)?.numeric ? 'desc' : 'asc' });

    const signed = (n) => {
        const s = fmt.number(n);
        return s !== null && n > 0 ? `+${s}` : s;
    };

    const distinct = (row, key) => new Set(row.members.map((m) => m[key])).size;

    const cell = (row, key, child) => {
        const grouped = !child && row.members?.length > 1;
        switch (key) {
            case 'symbol': {
                const expanded = open.has(row.asset);
                return (
                    <>
                        {grouped && (
                            <button
                                type="button"
                                className="crypto__expand"
                                aria-expanded={expanded}
                                aria-label={t(expanded ? 'crypto.collapse' : 'crypto.expand', { symbol: row.symbol })}
                                onClick={() => toggleOpen(row.asset)}
                            >
                                <span aria-hidden="true">{expanded ? '▾' : '▸'}</span>
                            </button>
                        )}
                        <Ticker row={row} />
                        <KindBadge row={row} />
                        {grouped
                            ? <span className="crypto__name">{tn('crypto.members', row.members.length)}</span>
                            : row.name && <span className="crypto__name">{row.name}</span>}
                    </>
                );
            }
            case 'chain':
                if (row.chain) return t(`crypto.chains.${row.chain}`);
                return grouped ? tn('crypto.chainCount', distinct(row, 'chain')) : null;
            case 'wallet':
                if (row.wallet) return row.wallet;
                return grouped ? tn('crypto.walletCount', distinct(row, 'walletId')) : null;
            case 'quantity': return formatQuantity(row.quantity, locale);
            case 'avgEntryPrice': return formatPrice(row.avgEntryPrice, locale);
            case 'currentPrice': return formatPrice(row.currentPrice, locale);
            case 'value': return fmt.number(row.value);
            case 'pnl': return row.pnl === null ? null : signed(row.pnl);
            case 'pnlPct': return formatPct(row.pnlPct, 1);
            case 'heldMs': return heldLabel(t, row.firstPurchaseAt);
            default: return null;
        }
    };

    return (
        <>
            <div className="positions__controls">
                {small.length > 0 && (
                    <div className="positions__filters" role="group" aria-label={t('crypto.balancesAria')}>
                        {[
                            [MAIN, t('crypto.balancesMain')],
                            [SMALL, t('crypto.balancesSmall', { total: fmt.number(smallTotal) })],
                        ].map(([key, label]) => (
                            <button
                                key={key}
                                type="button"
                                className={`positions__filter${showing === key ? ' is-active' : ''}`}
                                aria-pressed={showing === key}
                                onClick={() => setView(key)}
                            >
                                {label}
                            </button>
                        ))}
                    </div>
                )}
                <div className="positions__count">{tn('crypto.count', visible.length)}</div>
            </div>
            <div className="positions__scroll">
                <table className="positions">
                    <thead>
                        <tr>
                            {columns.map((column) => (
                                <th
                                    key={column.key}
                                    className={`is-${column.align}${sort.key === column.key ? ' is-sorted' : ''}`}
                                >
                                    <button type="button" onClick={() => toggleSort(column.key)}>
                                        {t(column.labelKey)}
                                        {sort.key === column.key && (
                                            <span aria-hidden="true">
                                                {sort.direction === 'asc' ? ' ↑' : ' ↓'}
                                            </span>
                                        )}
                                    </button>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {visible.flatMap((group) => {
                            const children = group.members.length > 1 && open.has(group.asset)
                                ? group.members
                                : [];
                            return [group, ...children].map((row, i) => {
                                const child = i > 0;
                                return (
                                    <tr
                                        key={child ? `${row.walletId}-${row.token}` : group.asset}
                                        className={child ? 'crypto__member' : undefined}
                                    >
                                        {columns.map((column) => {
                                            const content = cell(row, column.key, child);
                                            const n = row[column.key];
                                            const negative = column.signed && n !== null && n < 0;
                                            const positive = column.signed && n !== null && n > 0;
                                            return (
                                                <td
                                                    key={column.key}
                                                    className={`is-${column.align}${negative ? ' is-negative' : ''}${positive ? ' is-positive' : ''}`}
                                                    title={column.key === 'wallet' && row.walletId ? shortAddress(row.wallet) : undefined}
                                                >
                                                    {content ?? <span className="positions__empty">—</span>}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                );
                            });
                        })}
                    </tbody>
                </table>
            </div>
        </>
    );
}
